import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, teamLeaderNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { 
  FolderKanban, 
  Users, 
  Clock,
  MapPin,
  ArrowRight,
  Search,
  Sparkles,
  FileText,
  CheckCircle,
  XCircle,
  Hourglass,
  Building2,
  Calendar,
  DollarSign,
  Percent,
  Gift
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamLeaderDashboard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    
    const fetchData = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      setLoading(true);
      
      try {
        // Fetch applications first - this is the most important
        const applicationsRes = await axios.get(`${API}/projects-v3/team-leader/my-applications`, { headers });
        setApplications(applicationsRes.data || []);
      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
      }
      
      try {
        const statsRes = await axios.get(`${API}/dashboard/stats`, { headers });
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
      
      try {
        const assignmentsRes = await axios.get(`${API}/assignments`, { headers });
        const assignmentsData = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : [];
        setAssignments(assignmentsData);
        
        // Fetch project details
        const projectIds = [...new Set(assignmentsData.map(a => a.project_id))];
        const projectMap = {};
        for (const id of projectIds) {
          try {
            const res = await axios.get(`${API}/projects/${id}`, { headers });
            if (res?.data) projectMap[id] = res.data;
          } catch (e) {
            // Ignore individual project fetch errors
          }
        }
        setProjects(projectMap);
      } catch (error) {
        console.error("Error fetching assignments:", error);
        setAssignments([]);
      }
      
      // Fetch worker contributions for selected projects
      try {
        const applicationsRes = await axios.get(`${API}/projects-v3/team-leader/my-applications`, { headers });
        const selectedProjects = (applicationsRes.data || []).filter(a => a.status === "selected");
        
        const contribPromises = selectedProjects.map(async (app) => {
          try {
            const res = await axios.get(`${API}/projects-v3/team-contributions/${app.project_id}`, { headers });
            return { ...res.data, project_title: app.project_title };
          } catch (e) {
            return null;
          }
        });
        
        const contribResults = await Promise.all(contribPromises);
        setContributions(contribResults.filter(c => c !== null));
      } catch (error) {
        console.error("Error fetching contributions:", error);
      }
      
      setLoading(false);
    };

    fetchData();
  }, [token]);

  const getStatusBadge = (status) => {
    switch(status) {
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Hourglass className="w-3 h-3 mr-1" />Venter</Badge>;
      case "selected":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Valgt</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Avvist</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { style: 'decimal', minimumFractionDigits: 0 }).format(amount || 0) + ' kr';
  };

  if (loading) {
    return (
      <DashboardLayout navItems={teamLeaderNavItems} title={t('nav.dashboard')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate application stats
  const pendingApps = (applications || []).filter(a => a.status === "pending").length;
  const selectedApps = (applications || []).filter(a => a.status === "selected").length;

  return (
    <DashboardLayout navItems={teamLeaderNavItems} title={t('nav.dashboard')}>
      <div className="space-y-8" data-testid="teamleader-dashboard">
        {/* Browse Projects - Animated & Prominent */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#23F7DD] via-[#4F46E5] to-[#23F7DD] rounded-xl blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
          <Link to="/team-leader/browse-projects" className="block">
            <Card className="relative bg-gradient-to-br from-[#0A0A0A] to-[#111] border-0 overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#23F7DD] rounded-lg blur-md opacity-50 animate-ping"></div>
                      <div className="relative p-4 bg-[#23F7DD] rounded-lg">
                        <Search className="w-8 h-8 text-[#050505]" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-['Unbounded'] font-bold text-white">{t('projects.searchProjects')}</h3>
                        <Sparkles className="w-5 h-5 text-[#23F7DD] animate-pulse" />
                      </div>
                      <p className="text-[#94A3B8]">{t('projects.applyToProject')}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2 text-[#23F7DD] font-semibold">
                    <span>{t('common.view')}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg">
                  <FolderKanban className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                    {stats?.assigned_projects || 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('nav.assignments')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Hourglass className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-yellow-500">
                    {pendingApps}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('applications.pendingApplications')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {selectedApps}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('applications.approved')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <Users className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-purple-500">
                    {(assignments || []).reduce((sum, a) => sum + (a.worker_ids?.length || 0), 0)}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('nav.teams')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Applications */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              {t('applications.myApplications')} ({applications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-[#94A3B8]">{t('applications.noApplications')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('applications.applyToProjects')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.slice(0, 5).map((app) => (
                  <div
                    key={app.application_id}
                    className={`p-4 rounded-lg border transition-all ${
                      app.status === 'selected' 
                        ? 'bg-green-500/5 border-green-500/30' 
                        : app.status === 'rejected'
                        ? 'bg-red-500/5 border-red-500/30'
                        : 'bg-[#111] border-white/10 hover:border-[#23F7DD]/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-white font-semibold">{app.project_title}</h4>
                          {getStatusBadge(app.status)}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {app.client_company}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {app.location_city}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {app.crew_size} pers.
                          </span>
                          <span className="flex items-center gap-1 text-[#23F7DD]" title="Brutto lønn (før skatt)">
                            <DollarSign className="w-4 h-4" />
                            {formatCurrency(app.net_to_team || app.team_budget)} brutto
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-sm text-gray-500">
                          {app.applied_at && new Date(app.applied_at).toLocaleDateString('nb-NO')}
                        </span>
                        {app.status === 'selected' && (
                          <Link 
                            to={`/team-leader/my-projects/${app.project_id}`}
                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors"
                          >
                            {t('projects.viewProject')}
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {applications.length > 5 && (
                  <Link to="/team-leader/my-projects" className="block text-center text-[#23F7DD] hover:underline py-2">
                    {t('common.viewAll')} {applications.length} {t('applications.title').toLowerCase()} →
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Worker Contributions */}
        {contributions.length > 0 && contributions.some(c => c.contributions?.length > 0) && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-400" />
                {t('earnings.workerContributions') || 'Bidrag fra arbeidere'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {contributions.map((project) => (
                  project.contributions?.length > 0 && (
                    <div key={project.project_id} className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-white font-medium">{project.project_title}</h4>
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                          +{formatCurrency(project.total_tl_bonus)}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        {project.contributions.map((contrib, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">
                              {contrib.name} ({contrib.worker_code})
                            </span>
                            <span className="text-purple-300">
                              {contrib.tl_contribution_percent}% = {formatCurrency(contrib.contribution_amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-500/20 flex justify-between">
                        <span className="text-gray-400 text-sm">Din totale inntekt fra dette prosjektet:</span>
                        <span className="text-[#23F7DD] font-semibold">{formatCurrency(project.tl_total_earnings)}</span>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Projects */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">{t('projects.myProjects')}</CardTitle>
          </CardHeader>
          <CardContent>
            {assignments.length === 0 ? (
              <div className="text-center py-12">
                <FolderKanban className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('projects.noProjects')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => {
                  const project = projects[assignment.project_id];
                  return (
                    <Link
                      key={assignment.assignment_id}
                      to={`/team-leader/projects/${assignment.project_id}`}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
                    >
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-white">
                          {project?.title || `${t('projects.title')} #${assignment.project_id?.slice(-8)}`}
                        </h3>
                        {project && (
                          <div className="flex items-center gap-4 mt-2 text-sm text-[#94A3B8]">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {project.location_city}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {assignment.worker_ids?.length || 0} {t('nav.workers').toLowerCase()}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${
                            assignment.status === "completed"
                              ? "bg-green-500/20 text-green-400 border-green-500/30"
                              : assignment.status === "in_progress"
                              ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                              : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                          }`}
                        >
                          {assignment.status?.replace("_", " ")}
                        </span>
                        <ArrowRight className="w-5 h-5 text-[#475569] group-hover:text-[#23F7DD] transition-colors" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
